import { useState, useEffect, Dispatch, useMemo } from 'react';
import {
  FlexRow,
  OptionTile,
  RetirementAgeInput,
  RetirementAgeInputContainer,
} from '../Common';
import { QuestionTypeOption } from '../types';
import {
  currencyFormat,
  getPreviousQuestionsValue,
} from 'src/common/utils/functions';
import { ErrorObject } from 'src/containers/client-assessment/types';

type Props = {
  label: string;
  options?: QuestionTypeOption[];
  selectedQuestionId: string;
  questionnaireValue: any;
  setQuestionnaireValue: Dispatch<any>;
  attributeKey: string;
  questions: any[];
  setError: Dispatch<ErrorObject>;
};

const RETIREMENT_AGE = 67;

const getOptionLabel = (
  key: string,
  subKey: string,
  portfolioValue: number,
  declineAmountPercentage: number,
) => {
  if (key === 'investment_gain_preference') {
    if (subKey === 'guaranteed_gain')
      return `I tend to be more conservative and would prefer a guaranteed gain of $${currencyFormat(
        0.15 * portfolioValue,
      )}`;
    return `I tend to be more of a risk taker and would be willing to risk gaining nothing for the chance to gain $${currencyFormat(
      0.3 * portfolioValue,
    )}`;
  } else if (key === 'investment_loss_preference') {
    if (subKey === 'guaranteed_loss')
      return `I tend to be more conservative and would opt for a sure loss of $${currencyFormat(
        0.15 * portfolioValue,
      )}`;
    return ` I tend to be more of a risk taker and am open to the possibility of losing $${currencyFormat(
      0.3 * portfolioValue,
    )} if it means there is a chance of not losing anything.`;
  }
};

function Radio({
  label,
  options = [],
  setQuestionnaireValue,
  questionnaireValue,
  selectedQuestionId,
  attributeKey,
  questions,
  setError,
}: Props) {
  const clientCurrentAge = useMemo(() => {
    if (questions && questionnaireValue) {
      const enteredAge = getPreviousQuestionsValue(
        'age',
        questions,
        questionnaireValue,
      );

      return enteredAge ?? 67;
    }
    return 0;
  }, [questionnaireValue, questions]);

  const [value, setValue] = useState<any>();
  const [isAgeValid, setIsAgeValid] = useState(false);
  const [age, setAge] = useState<number | ''>(RETIREMENT_AGE);
  const [newOptions, setNewOptions] = useState<any[]>([]);
  const [isSelectedOptionRetirement, setIsSelectedOptionRetirement] =
    useState(false);

  useEffect(() => {
    if (
      selectedQuestionId &&
      questionnaireValue &&
      attributeKey === 'investment_period'
    ) {
      const defaultAge =
        questionnaireValue[selectedQuestionId] || RETIREMENT_AGE;

      const isAgeInRange = age => age >= clientCurrentAge && age <= 100;
      if (![2, 9, 11].includes(defaultAge)) {
        handleErrorSuccess(!isAgeInRange(+defaultAge), clientCurrentAge, false);
        setAge(+defaultAge ?? RETIREMENT_AGE);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuestionId]);

  useEffect(() => {
    let currentQuestionValue = questionnaireValue[selectedQuestionId];
    if (
      currentQuestionValue &&
      Array.isArray(currentQuestionValue) &&
      currentQuestionValue.length > 0
    ) {
      currentQuestionValue =
        currentQuestionValue[0].value?.[0]?.value ??
        currentQuestionValue[0].value;
      setValue(currentQuestionValue);
    }
    if (
      ['investment_loss_preference', 'investment_gain_preference'].includes(
        attributeKey,
      )
    ) {
      const portfolioValue = getPreviousQuestionsValue(
        'investment_amount',
        questions,
        questionnaireValue,
      );
      const investmentAmountDecline = getPreviousQuestionsValue(
        'investment_amount_decline',
        questions,
        questionnaireValue,
      );
      setNewOptions(
        options.map(option => ({
          value: option.value,
          label: getOptionLabel(
            attributeKey,
            option?.id ?? '',
            portfolioValue,
            investmentAmountDecline,
          ),
        })),
      );
    } else {
      setNewOptions(options);
    }
    if (
      currentQuestionValue ||
      currentQuestionValue === 0 ||
      currentQuestionValue === false
    ) {
      if (
        Array.isArray(currentQuestionValue) &&
        currentQuestionValue.length === 0
      ) {
        setValue('none');
      } else
        setValue(
          attributeKey == 'investment_gain_preference' ||
            attributeKey == 'investment_loss_preference'
            ? `${currentQuestionValue}`
            : currentQuestionValue,
        );
    } else if (Array.isArray(options) && options.length) {
      setValue(options[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeKey]);
  useEffect(() => {
    setQuestionnaireValue({
      ...questionnaireValue,
      [selectedQuestionId]: value === 'none' ? [] : value,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeKey, value]);

  const handleErrorSuccess = (
    isError,
    clientCurrentAge,
    displayErrorState = true,
  ) => {
    setError({
      valid: isError,
      message: `Enter a value between ${clientCurrentAge} and 100`,
    });
    displayErrorState && setIsAgeValid(isError);
  };
  const handleErrorFailure = () => {
    setIsAgeValid(false);
    setError({
      valid: false,
      message: '',
    });
  };

  const handleChangeItem = (itemInfo: string) => {
    // Special Case for "What is your investment time horizon?" question
    if (attributeKey === 'investment_period') {
      const isSelectedQuestionOfRetirement = age === itemInfo;
      setIsSelectedOptionRetirement(+itemInfo === 67);
      if (isSelectedQuestionOfRetirement || +itemInfo === 67) {
        const isAgeInRange = +age < clientCurrentAge || +age > 100;
        handleErrorSuccess(isAgeInRange, clientCurrentAge);
      } else {
        handleErrorFailure();
        setAge(age);
      }
      if (+itemInfo === 67 && value !== itemInfo) {
        setValue(age);
      } else if (value !== itemInfo) {
        setValue(itemInfo);
      }
    }
    // Normal Cases
    else if (value !== itemInfo) {
      setValue(itemInfo);
    }
  };

  const handleRetirementAge = (age: number) => {
    setAge(age ? age : '');
    if (clientCurrentAge) {
      const isAgeInRange = age >= clientCurrentAge && age <= 100;

      handleErrorSuccess(!isAgeInRange, clientCurrentAge);
      isAgeInRange && age ? setValue(age) : setValue(value);
    } else handleErrorFailure();
  };

  return (
    <FlexRow justify="flex-start" height={true}>
      {Array.isArray(newOptions) && newOptions.length > 0 ? (
        newOptions.map((option, index) => {
          const isLastOption = index === newOptions?.length - 1;

          const isSelected =
            (!isSelectedOptionRetirement && option?.value === value) ||
            (attributeKey === 'investment_period' &&
              isLastOption &&
              ![2, 9, 11].includes(value));

          return (
            <OptionTile
              onClick={() => handleChangeItem(option.value)}
              selected={isSelected ?? false}
              key={index.toString()}
              direction={isLastOption ? 'row' : 'column'}
              alignItems={isAgeValid && isLastOption ? 'flex-start' : 'center'}
            >
              <span>{option.label}</span>
              {isLastOption && attributeKey === 'investment_period' ? (
                <RetirementAgeInputContainer>
                  <RetirementAgeInput
                    type="number"
                    value={age}
                    onChange={e => handleRetirementAge(+e.target.value)}
                    disabled={!isSelectedOptionRetirement}
                  />
                  {isAgeValid && (
                    <p className="error-msg">
                      Enter a value between {clientCurrentAge ?? 0} and 100
                    </p>
                  )}
                </RetirementAgeInputContainer>
              ) : (
                <></>
              )}
            </OptionTile>
          );
        })
      ) : (
        <></>
      )}
    </FlexRow>
  );
}

export default Radio;
